import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CertificacionPrecio } from "../models/PlanesUsuario/CertificacionPrecio";
import { InitialData } from "../models/PlanesUsuario/InitialData";
import { Plan } from "../models/PlanesUsuario/Plan";
import { PlanFacturacion } from "../models/PlanesUsuario/PlanFacturacion";
import { PlanUsuario } from "../models/PlanesUsuario/PlanUsuario";
import { Resumen, SolicitudCalculoPlan } from "../models/PlanesUsuario/Resumen";
import { Reseller } from "../models/Reseller/Reseller";
import { ResellerFilter } from "../models/Reseller/ResellerFilter";
import { Page } from "../shared/models/Page";
import { PagedResponse } from "../shared/models/PagedResponse";
import { Response } from "../shared/models/Response";
import { SoporteButton } from "../shared/models/SoporteButton.enum";
import { Cupon } from "../models/PlanesUsuario/Cupon";
import { CambioPlanPartner } from "../models/PlanesUsuario/CambioPlanPartner";
import { Adicional } from "../models/Emisor/Adicional";
import { SessionUser } from "../shared/models/sessionUser";
import { TokenStorageService } from "./token-storage.service";
import { DteMonthData } from "../models/Emisor/DteMonthData";
import { CertificacionCliente } from "../models/PlanesUsuario/CertificacionCliente";
import { CertificacionInstructivo } from "../models/PlanesUsuario/CertificacionInstructivo";
import { DeudaAdicionalPendiente } from "../models/PlanesUsuario/DeudaAdicionalPendiente";
import { DeudaGraciaPendiente } from "../models/PlanesUsuario/DeudaGraciaPendiente";
import { CotizacionPlan, CotizarPlanRequest } from "../models/PlanesUsuario/CotizacionPlan";
import { PlanPaymentState } from "../models/PlanesUsuario/PlanPaymentState";

@Injectable({
	providedIn: "root",
})
export class PlanUsuarioService {
	private REST_API_SERVER = environment.backend_server + "/planusuario";
	// private buttonActionsSubject: Subject<SoporteButton>;
	// public buttonActionsObservable: Observable<SoporteButton>;

	constructor(private http: HttpClient, private tokenService: TokenStorageService) {
		// this.buttonActionsSubject = new Subject<SoporteButton>();
		// this.buttonActionsObservable = this.buttonActionsSubject.asObservable();
	}

	getDataByUser(idUsuario: string) {
		return this.http.get<Array<PlanUsuario>>(this.REST_API_SERVER + "/" + idUsuario);
	}

	getContratacionDataByCompany(idEmisor: string) {
		return this.http.get<Array<PlanUsuario>>(this.REST_API_SERVER + "/company/" + idEmisor);
	}

	getPlanesByCompany(idEmisor: string) {
		return this.http.get<Array<PlanUsuario>>(this.REST_API_SERVER + "/company/plan/" + idEmisor);
	}

	getPaymentStateByCompany(idEmisor: string): Observable<PlanPaymentState> {
		return this.http.get<PlanPaymentState>(`${this.REST_API_SERVER}/company/payment-state/${idEmisor}`);
	}
	
	getFacturasByPlan(idPlan: string) {
		return this.http.get<Array<PlanFacturacion>>(this.REST_API_SERVER + "/facturas/" + idPlan);
	}

	getPendingAdditionalDebt(emisorId: string): Observable<DeudaAdicionalPendiente> {
		return this.http
			.get<any>(`${this.REST_API_SERVER}/deuda-adicionales/${emisorId}`)
			.pipe(map((data) => this.normalizePendingAdditionalDebt(data)));
	}

	payPendingAdditionalDebt(payload: {
		idUsuario: string;
		idEmisor: string;
		idDatosFacturacion: string;
		idPlanUsuario?: string;
	}): Observable<Response<any>> {
		return this.http.post<Response<any>>(`${this.REST_API_SERVER}/deuda-adicionales/payment`, payload);
	}

	getDeudaGracia(emisorId: string, planId: string): Observable<DeudaGraciaPendiente> {
		return this.http
			.get<any>(`${this.REST_API_SERVER}/deuda-gracia/${emisorId}/${planId}`)
			.pipe(map((data) => this.normalizeDeudaGracia(data)));
	}

	payDeudaGracia(payload: {
		idUsuario: string;
		idEmisor: string;
		idDatosFacturacion: string;
		idPlanUsuario: string;
	}): Observable<Response<any>> {
		return this.http.post<Response<any>>(`${this.REST_API_SERVER}/deuda-gracia/payment`, payload);
	}

	getFacturaPDF(idFactura: string): Observable<Response<any>> {
		return this.http.get<Response<any>>(this.REST_API_SERVER + "/facturas/pdf/" + idFactura);
	}

	cancelarPlan(idPlan: string): Observable<Response<any>> {
		return this.http.get<Response<any>>(this.REST_API_SERVER + "/cancelar/" + idPlan);
	}

	cambiarPlanPartner(model: CambioPlanPartner): Observable<Response<any>> {
		return this.http.post<Response<any>>(this.REST_API_SERVER + "/partner/change-plan", model);
	}

	checkUsers(idUsuario: string, idEmisor: string): Observable<boolean> {
		return this.http.get<boolean>(this.REST_API_SERVER + "/usuarios/" + idUsuario + "/" + idEmisor);
	}

	getCurrentDocuments(idPlanUsuario: string): Observable<any> {
		return this.http.get<any>(this.REST_API_SERVER + "/currentdte/" + idPlanUsuario);
	}

	getInitialData() {
		return this.http.get<InitialData>(this.REST_API_SERVER + "/initialdata");
	}

	cotizarPlan(request: CotizarPlanRequest): Observable<CotizacionPlan> {
		return this.http.post<CotizacionPlan>(`${this.REST_API_SERVER}/cotizacion`, request);
	}

	getCupon(cupon: string) {
		return this.http.get<Cupon>(this.REST_API_SERVER + "/cupones/" + cupon);
	}

	doPayment(resumen: Resumen): Observable<Response<any>> {
		return this.http.post<Response<any>>(this.REST_API_SERVER + "/payment", resumen);
	}
	purchaseCertificationsPayment(resumen: Resumen): Observable<Response<any>> {
		return this.http.post<Response<any>>(this.REST_API_SERVER + "/payment-certificaciones", resumen);
	}

	purchaseAdicionalesPayment(payload: any): Observable<Response<any>> {
		return this.http.post<Response<any>>(this.REST_API_SERVER + "/payment-adicionales", payload);
	}

	quoteAdicionales(payload: any): Observable<any> {
		return this.http.post<any>(this.REST_API_SERVER + "/adicionales/cotizar", payload);
	}

	confirmPaykuDirecto(payload: any): Observable<Response<any>> {
		return this.http.post<Response<any>>(this.REST_API_SERVER + "/insert-adicional", payload);
	}

	getAdicionalesCatalogo(isPartner: boolean = false): Observable<any[]> {
		return this.http.get<any[]>(`${this.REST_API_SERVER}/adicionales/catalogo?isPartner=${isPartner}`);
	}

	getCertificacionesCliente(idPlanUsuario: string): Observable<CertificacionCliente[]> {
		return this.http.get<CertificacionCliente[]>(`${this.REST_API_SERVER}/certificaciones/cliente/${idPlanUsuario}`);
	}

	getInstructivoCertificacion(idPlanUsuario: string, certificacionId: string): Observable<Response<CertificacionInstructivo>> {
		return this.http.get<Response<CertificacionInstructivo>>(
			`${this.REST_API_SERVER}/certificaciones/instructivo/${idPlanUsuario}/${certificacionId}`
		);
	}

	reenviarInstructivoCertificacion(payload: {
		idPlanUsuario: string;
		certificacionId: string;
		correoDestino?: string;
	}): Observable<Response<any>> {
		return this.http.post<Response<any>>(`${this.REST_API_SERVER}/certificaciones/instructivo/reenviar`, payload);
	}

	getActualPlan() {
		let planActivo: PlanUsuario;
		let _userSession = this.tokenService.getUser() as SessionUser;
		if (_userSession.planes && _userSession.planes.length > 0) {
			for (let i = _userSession.planes.length - 1; i >= 0; i--) {
				if (_userSession.planes[i].emisorId === this.tokenService.getCompany()) {
					planActivo = _userSession.planes[i];
					break;
				}
			}
			if (!planActivo) {
				planActivo = _userSession.planes[0];
			}
		}
		return planActivo;
	}
	getDteChartData(emisorId: string): Observable<Response<DteMonthData[]>> {
		return this.http.get<Response<DteMonthData[]>>(
			`${this.REST_API_SERVER}/dtechart/${emisorId}?monthsCount=6`
		);
	  }
	getCertificacionesCompra(emisorId: string) {
		return this.http.get<{ certificaciones: CertificacionPrecio[] }>(
			this.REST_API_SERVER + `/certificaciones/compra/${emisorId}`
		);
	}
	getCalculosCertificacionesCompra(payload: {
		idEmisor: string;
		idDatosFacturacion: string;
		idPlanUsuario: string;
		certificaciones: CertificacionPrecio[];
	}): Observable<Resumen> {
		return this.http.post<Resumen>(
			`${this.REST_API_SERVER}/calculos/certificaciones`,
			payload
		);
	}

	private normalizeDeudaGracia(data: any): DeudaGraciaPendiente {
		const deuda = new DeudaGraciaPendiente();
		if (!data) {
			// Sin plan vencido: no hay decisión de deuda que mostrar
			deuda.tieneDeuda = false;
			deuda.ventanaEnCurso = false;
			return deuda;
		}
		deuda.id = data?.id ?? data?.Id ?? null;
		deuda.emisorId = data?.emisorId ?? data?.EmisorId ?? "";
		deuda.planUsuarioId = data?.planUsuarioId ?? data?.PlanUsuarioId ?? "";
		deuda.nombrePlan = data?.nombrePlan ?? data?.NombrePlan ?? "";
		deuda.fechaVencimientoPlan = data?.fechaVencimientoPlan ?? data?.FechaVencimientoPlan ?? null;
		deuda.fechaFinGracia = data?.fechaFinGracia ?? data?.FechaFinGracia ?? null;
		deuda.cantidadDtes = Number(data?.cantidadDtes ?? data?.CantidadDtes ?? 0);
		deuda.cantidadBhes = Number(data?.cantidadBhes ?? data?.CantidadBhes ?? 0);
		deuda.precioMensualBase = Number(data?.precioMensualBase ?? data?.PrecioMensualBase ?? 0);
		deuda.montoNeto = Number(data?.montoNeto ?? data?.MontoNeto ?? 0);
		deuda.montoIva = Number(data?.montoIva ?? data?.MontoIva ?? 0);
		deuda.montoTotal = Number(data?.montoTotal ?? data?.MontoTotal ?? 0);
		deuda.estado = Number(data?.estado ?? data?.Estado ?? 0);
		deuda.tieneDeuda = Boolean(data?.tieneDeuda ?? data?.TieneDeuda);
		deuda.ventanaEnCurso = Boolean(data?.ventanaEnCurso ?? data?.VentanaEnCurso);
		deuda.fechaPago = data?.fechaPago ?? data?.FechaPago ?? null;
		return deuda;
	}

	private normalizePendingAdditionalDebt(data: any): DeudaAdicionalPendiente {
		const deuda = new DeudaAdicionalPendiente();
		deuda.emisorId = data?.emisorId ?? data?.EmisorId ?? "";
		deuda.tieneDeuda = Boolean(data?.tieneDeuda ?? data?.TieneDeuda);
		deuda.cantidadFacturas = Number(data?.cantidadFacturas ?? data?.CantidadFacturas ?? 0);
		deuda.totalPendiente = Number(data?.totalPendiente ?? data?.TotalPendiente ?? 0);
		deuda.puedeRegularizarEnApp = Boolean(data?.puedeRegularizarEnApp ?? data?.PuedeRegularizarEnApp);
		deuda.mensaje = data?.mensaje ?? data?.Mensaje ?? "";
		deuda.facturas = ((data?.facturas ?? data?.Facturas ?? []) as any[]).map((factura) => ({
			folio: Number(factura?.folio ?? factura?.Folio ?? 0),
			facturaId: factura?.facturaId ?? factura?.FacturaId ?? "",
			transbankId: factura?.transbankId ?? factura?.TransbankId ?? "",
			planUsuarioId: factura?.planUsuarioId ?? factura?.PlanUsuarioId ?? "",
			referencia: factura?.referencia ?? factura?.Referencia ?? "",
			tipo: factura?.tipo ?? factura?.Tipo ?? "",
			total: Number(factura?.total ?? factura?.Total ?? 0),
			fechaEmision: factura?.fechaEmision ?? factura?.FechaEmision ?? null,
			fechaPago: factura?.fechaPago ?? factura?.FechaPago ?? null,
			pagada: Boolean(factura?.pagada ?? factura?.Pagada),
			vencida: Boolean(factura?.vencida ?? factura?.Vencida),
		}));
		return deuda;
	}
}
